import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('INR');
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (product) => {
    return currency === 'INR' ? product.price_inr : product.price_usd;
  };

  const getCurrencySymbol = () => {
    return currency === 'INR' ? '₹' : '$';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-12">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-2">Our Collection</h1>
              <p className="text-gray-400">Premium cosmic-themed t-shirts</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Currency:</span>
              <select 
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="bg-transparent border border-gray-700 rounded-lg px-3 py-2"
                data-testid="currency-selector"
              >
                <option value="INR" className="bg-gray-900">INR (₹)</option>
                <option value="USD" className="bg-gray-900">USD ($)</option>
              </select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div 
                key={product.id} 
                className="glass-card group cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="relative overflow-hidden rounded-xl mb-4 bg-white p-4">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-80 object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-20">
                    Limited Edition
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
                <p className="text-gray-400 mb-4 line-clamp-2">{product.description}</p>
                
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-2xl font-bold">
                      {getCurrencySymbol()}{getPrice(product)}
                    </div>
                    <div className="text-sm text-gray-400">Per piece</div>
                  </div>
                  <button 
                    className="btn-primary flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/product/${product.id}`);
                    }}
                    data-testid={`view-product-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    View
                  </button>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <span className="text-sm text-gray-400">Sizes:</span>
                    {product.sizes.slice(0, 5).map((size) => (
                      <span key={size} className="text-xs px-2 py-1 bg-gray-800 rounded">{size}</span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-gray-400">Colors:</span>
                    {product.colors.slice(0, 5).map((color) => (
                      <div 
                        key={color.name}
                        className="w-6 h-6 rounded-full border-2 border-gray-600"
                        style={{ backgroundColor: color.hex }}
                        title={color.name}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
